<template>
  <div
    :class="['email-component', 'billing-info-component', { selected }]"
    :style="computedStyles"
    @click="handleClick"
  >
    <div class="billing-info-wrapper">
      <!-- Customer Info Section -->
      <div class="customer-section">
        <div class="customer-name" :style="nameStyles">Jane Doe</div>
        <div class="customer-detail" :style="detailStyles">
          <span class="detail-label">{{ getLabel('email') }}: </span>
          <a href="#" :style="linkStyles">
            customer@gmail.com
          </a>
        </div>
        <div class="customer-detail" :style="detailStyles">
          <span class="detail-label">{{ getLabel('phone') }}: </span>
          <span>-</span>
        </div>
      </div>

      <!-- Transaction Info Section -->
      <div class="transaction-section">
        <div class="transaction-detail" :style="transactionStyles">
          <span class="detail-label">{{ getLabel('transactionReference') }}: </span>
          <span>#92100277723</span>
        </div>
        <div class="transaction-detail" :style="transactionStyles">
          <span class="detail-label">{{ getLabel('transactionDate') }}: </span>
          <span>September 05, 2025</span>
        </div>
        <div class="transaction-detail" :style="transactionStyles">
          <span class="detail-label">{{ getLabel('paymentStatus') }}: </span>
          <span class="payment-status" :style="statusStyles">UNPAID</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BillingInfoComponent',
  props: ['componentData', 'selected'],
  data() {
    return {
      labels: {
        english: {
          email: 'Email',
          phone: 'Phone',
          transactionReference: 'Transaction reference',
          transactionDate: 'Transaction date',
          paymentStatus: 'Payment status'
        },
        spanish: {
          email: 'Correo',
          phone: 'Teléfono',
          transactionReference: 'Referencia de transacción',
          transactionDate: 'Fecha de transacción',
          paymentStatus: 'Estado del pago'
        },
        french: {
          email: 'Email',
          phone: 'Téléphone',
          transactionReference: 'Référence de transaction',
          transactionDate: 'Date de transaction',
          paymentStatus: 'Statut du paiement'
        },
        swedish: {
          email: 'E-post',
          phone: 'Telefon',
          transactionReference: 'Transaktionsreferens',
          transactionDate: 'Transaktionsdatum',
          paymentStatus: 'Betalningsstatus'
        },
        danish: {
          email: 'E-mail',
          phone: 'Telefon',
          transactionReference: 'Transaktionsreference',
          transactionDate: 'Transaktionsdato',
          paymentStatus: 'Betalingsstatus'
        },
        greek: {
          email: 'Email',
          phone: 'Τηλέφωνο',
          transactionReference: 'Αναφορά συναλλαγής',
          transactionDate: 'Ημερομηνία συναλλαγής',
          paymentStatus: 'Κατάσταση πληρωμής'
        }
      }
    }
  },
  computed: {
    currentLanguage() {
      return this.componentData.language || 'english'
    },

    computedStyles() {
      const styles = this.componentData.styles || {}
      const backgroundColor = styles.backgroundColor === 'inherit' || !styles.backgroundColor ?
        'transparent' : styles.backgroundColor

      return {
        backgroundColor: backgroundColor,
        padding: `${styles.paddingTop || '16'}px ${styles.paddingRight || '16'}px ${styles.paddingBottom || '16'}px ${styles.paddingLeft || '16'}px`,
        marginTop: `${styles.marginTop || '16'}px`,
        marginBottom: `${styles.marginBottom || '16'}px`,
        borderRadius: `${styles.borderRadius || '8'}px`,
        border: styles.showBorder ? `1px solid ${styles.borderColor || '#e9ecef'}` : 'none',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    nameStyles() {
      const styles = this.componentData.styles || {}
      return {
        fontSize: `${styles.nameFontSize || '16'}px`,
        fontWeight: '600',
        marginBottom: '8px',
        color: styles.nameColor || '#333333',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    detailStyles() {
      const styles = this.componentData.styles || {}
      return {
        fontSize: `${styles.fontSize || '14'}px`,
        color: styles.textColor || '#666666',
        marginBottom: '4px',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    transactionStyles() {
      const styles = this.componentData.styles || {}
      return {
        fontSize: `${styles.fontSize || '14'}px`,
        color: styles.textColor || '#666666',
        marginBottom: '4px',
        textAlign: 'right',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    linkStyles() {
      const styles = this.componentData.styles || {}
      return {
        color: styles.linkColor || '#007bff',
        textDecoration: 'none'
      }
    },

    statusStyles() {
      const styles = this.componentData.styles || {}
      // Always show as unpaid since it's hardcoded
      const statusColor = styles.statusColorUnpaid || '#dc3545'

      return {
        color: statusColor,
        fontWeight: '600',
        textTransform: 'uppercase'
      }
    }
  },
  methods: {
    handleClick() {
      this.$emit('click')
    },

    getLabel(key) {
      const languageLabels = this.labels[this.currentLanguage] || this.labels.english
      return languageLabels[key] || key
    }
  }
}
</script>

<style scoped>
.billing-info-component {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  width: 100%;
  box-sizing: border-box;
}

.billing-info-component:hover,
.billing-info-component.selected {
  border-color: #e14eca !important;
  box-shadow: 0 0 0 4px rgba(225, 78, 202, 0.1);
}

.billing-info-wrapper {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.customer-section {
  flex: 1;
  min-width: 200px;
}

.transaction-section {
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.customer-detail,
.transaction-detail {
  line-height: 1.5;
}

.detail-label {
  font-weight: 500;
}

.payment-status {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 12px;
  letter-spacing: 0.5px;
}

@media (max-width: 600px) {
  .billing-info-wrapper {
    flex-direction: column;
  }

  .transaction-section {
    align-items: flex-start;
  }

  .transaction-detail {
    text-align: left !important;
  }
}
</style>
